import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import UserData from '../interfaces/UserData'
import { useAuth } from '../hooks/useAuth'
import Reservation, { Additional, Payment } from '../interfaces/Reservation'
import { formatDateToYYYY_MM_DD, formatWithThousandSep, MessageCrea } from '../utils/utils'
import Vehicle from '../interfaces/Vehicle'
import { FormatProps, handleInputChangeFormat } from '../utils/handleInputChangeFormat'
import useReservationCrea from '../hooks/useReservationCrea'
import Client from '../interfaces/Client'
import { AppPath } from '../enums/AppPath'
import useVehicleFetcher from '../hooks/useVehicleFetcher'
import { NvvAction, ReservaAction } from '../enums/DocActions'
import { generatePayVehicle, tradeInToPaycost, transformToTradeIn } from '../utils/reservationNvvShared'
import Deal from '../interfaces/Deal'

import InputTextArea from './InputTextArea'
import InputField from './InputField'
import ReservationPago from './ReservationPago'
import ReservationAdicional from './ReservationAdicional'
import ReservationVehiculo from './ReservationVehiculo'
import { ButtonAction, ButtonCheckConfirma, ButtonMenuSet, ButtonPrint } from './Buttons'
import { ReservationPreview } from './ReservationPreview'
import { Info } from './MessageInfo'
import VehicleCard from './VehicleCard'
import { ClientCard } from './ClientCard'
import VehicleEdit from './VehicleEdit'
import DocConfirma from './DocConfirma'
import { TotalDisplay } from './SalenoteDetail'
import { DisplayValueVert } from './DisplayValue'
import DisplayError from './DisplayError'

export interface ReservationFormat extends Reservation {
  price_format: string
  amount_format: string
  vehicles?: PayVehicle[]
}
export interface PayVehicle {
  key: string
  description: string
  price: number
  price_format: string
  patente?: string
  vehicle?: Vehicle | null
  //  fromReservation?:boolean

  // NEW: Trade-in purchase tracking
  associatedPurchaseId?: string
  purchaseCreated?: boolean
  purchaseNumber?: number
}

interface Props {
  vehicle: Vehicle
  client: Client
  reservation?: Reservation
  nvvInd: NvvAction
  handleAnular?: (motivo: string) => void
  handleArchivar?: () => void
  onRefresh?: () => void
  // El siguiente cuando estamos creando desde el Negocio
  onCreateModify?: (id: string) => void
  deal?: Deal
}

export const ReservationNvvEdit = ({
  vehicle,
  client,
  reservation,
  nvvInd,
  handleAnular,
  handleArchivar,
  onRefresh,
  onCreateModify,
  deal,
}: Props) => {
  const { userData, selectedPair } = useAuth()
  const [formData, setFormData] = useState<ReservationFormat | null>(null)
  const appraisal = vehicle.appraisal ?? 0
  const [payments, setPayments] = useState<Payment[]>([])
  const [costs, setCosts] = useState<Additional[]>([])
  const [vehicles, setVehicles] = useState<PayVehicle[]>([])
  const [showPreview, setShowPreview] = useState<boolean>(false)
  // const [reservationEx, setReservationEx] = useState<ReservationEx | null>(null)
  const [alertMessage, setAlertMessage] = useState('')
  const [infoMessage, setInfoMessage] = useState('')
  const { errorReservationCrea, reservationCrea } = useReservationCrea()
  const [modificandoSw, setModificandoSw] = useState<boolean>(false)
  const [vehicleShow, setVehicleShow] = useState<boolean>(false)
  //BEGIN lo siguiente es parte del estado de ReservationVehiculo
  // que mantenemos aca para poder llamar a la que crea vehoculo
  const [vehShowVehicleEdit, setVehShowVehicleEdit] = useState<boolean>(false)
  const [vehNewPayment, setVehNewPayment] = useState<PayVehicle | null>(generatePayVehicle)
  // Errores de validacion de los input
  const [vehErrors, setVehErrors] = useState<{ [key: string]: string }>({})
  const [vehVehicle, setVehVehicle] = useState<Vehicle | null>(null)
  const { errorVehicle, fetchVehicle } = useVehicleFetcher()
  const [totalCost, setTotalCost] = useState<number>(0)
  const [totalPayments, setTotalPayments] = useState<number>(0)
  //END----------------------------------

  // Aca colocamos los valores iniciales.
  useEffect(() => {
    if (reservation) {
      const price_format = formatWithThousandSep(reservation.price)
      const amount_format = formatWithThousandSep(reservation.amount)
      setFormData({ ...reservation, price_format: price_format, amount_format: amount_format })
      setPayments(reservation.payments ?? [])
      setCosts(reservation.additionals ?? [])
      setVehicles(tradeInToPaycost(reservation.tradeIns ?? [], reservation.tradeInVehicles ?? []))

      // const allCost  = totalCostCalc(reservation.additionals ?? [],price_format)
      // setTotalCost(allCost)
      if (nvvInd !== NvvAction.CreaFromRes) {
        vistaPreviaClick()
      } else if (formData) {
        setFormData({ ...formData, notes: '' })
      }
    } else {
      const formData = reservationInitial(userData, vehicle, client, undefined, deal)
      setFormData(formData)
      const allCost = totalCostCalc([], formData.price_format)
      setTotalCost(allCost)
    }
  }, [selectedPair, userData, reservation])

  // Effect to update alertMessage whenever errorReservationCrea changes
  useEffect(() => {
    if (errorReservationCrea) {
      setAlertMessage(errorReservationCrea.message)
    }
  }, [errorReservationCrea])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name } = e.target

    const formatProps: FormatProps[] = [{ name: 'price', name_format: 'price_format', isDecimal: false }]
    handleInputChangeFormat<ReservationFormat>(e, formData, setFormData, formatProps)

    if (name !== 'price_format') return

    if (formData) {
      const allCost = totalCostCalc(costs, e.target.value)
      setTotalCost(allCost)
      setFormData((prevFormData) => {
        if (!prevFormData) return null // To satisfy TypeScript null-checks

        return {
          ...prevFormData,
          amount_format: formatWithThousandSep(calcAmount(null, null, null, prevFormData?.price)),
        }
      })
    }
  }

  const totalCostCalc = (costArray: Additional[], price: string) => {
    const suma = costArray.reduce((sum, current) => sum + Number(current?.cost ?? 0), 0)
    let numericValue = price.replace(/[^\d]/g, '')

    if (numericValue == '') numericValue = '0'

    return suma + Number(numericValue)
  }

  const calcAmount = (
    updatedCosts: Additional[] | null,
    updatedPayments: Payment[] | null,
    updatedVehicles: PayVehicle[] | null,
    price: number
  ): number => {
    const sumTot = sumAddPagosVeh(updatedCosts, updatedPayments, updatedVehicles)

    return -1 * (Number(sumTot) + Number(price))
  }

  /*
    llega cuando huno un cambio en los Vehiculos
    */
  const handleVehiclesChange = (updatedVehicles: typeof vehicles) => {
    setVehicles(updatedVehicles)

    if (formData !== null) {
      const array = updatedVehicles
      const suma = array.reduce((sum, current) => sum + Number(current?.price ?? 0), 0)
      const suma2 = payments.reduce((sum, current) => sum + Number(current?.value ?? 0), 0)

      setTotalPayments(Number(suma) + Number(suma2))

      setFormData((prevFormData) => {
        if (!prevFormData) return null // To satisfy TypeScript null-checks

        return {
          ...prevFormData,

          amount: calcAmount(null, null, updatedVehicles, prevFormData?.price),
          amount_format: formatWithThousandSep(calcAmount(null, null, updatedVehicles, prevFormData?.price)),
        }
      })
    }
  }

  /*
   llega cuando hubo un cambio en los Pagos
   */
  const handlePaymentsChange = (updatedPayments: typeof payments) => {
    setPayments(updatedPayments)
    // Le sumamos al saldo

    if (formData !== null) {
      const array = updatedPayments
      const suma = array.reduce((sum, current) => sum + Number(current?.value ?? 0), 0)
      const suma2 = vehicles.reduce((sum, current) => sum + Number(current?.price ?? 0), 0)

      setTotalPayments(Number(suma) + Number(suma2))
      setFormData((prevFormData) => {
        if (!prevFormData) return null // To satisfy TypeScript null-checks

        return {
          ...prevFormData,
          payments: updatedPayments,
          amount_format: formatWithThousandSep(calcAmount(null, updatedPayments, null, prevFormData?.price)),
        }
      })
    }
  }

  /*
    llega cuando hubo un cambio en los Adicionales
    */
  const handleCostsChange = (updatedCosts: typeof costs) => {
    setCosts(updatedCosts)

    if (formData !== null) {
      const allCost = totalCostCalc(updatedCosts, formData.price_format)
      setTotalCost(allCost)

      setFormData((prevFormData) => {
        if (!prevFormData) return null // To satisfy TypeScript null-checks

        return {
          ...prevFormData,
          additionals: updatedCosts,
          amount: calcAmount(updatedCosts, null, null, prevFormData?.price),
          amount_format: formatWithThousandSep(calcAmount(updatedCosts, null, null, prevFormData?.price)),
        }
      })
    }
  }

  const vistaPreviaClick = () => {
    // Ahora, le colocamos a la reserva los datos del
    // los adicionales, pagos y vehiculos

    setFormData((prevFormData) => {
      if (!prevFormData) return null // To satisfy TypeScript null-checks

      return {
        ...prevFormData,
        vehicles: vehicles,
      }
    })
    setShowPreview(true)
  }
  // Aca es cuando se presiona el "Crear en la Preview"
  const navigate = useNavigate()
  const handleCrearOModificar = async () => {
    if (formData === null) return

    reservationFill()

    const accion = creandoSw ? 0 : 1

    // Devuelve el ID cuyando estamos creando,
    // nada importante si estamos modificando
    // Si devuelve vacio, nos fue mal.

    const res = await reservationCrea(formData, accion)

    // refrescamos la pantalla
    if (res === null) {
      return
    }
    const id = creandoSw ? res : reservation._id

    if (onRefresh) {
      onRefresh()
    } else if (onCreateModify) {
      onCreateModify(id)
    } else {
      const url = `/${AppPath.Reservations}/view/${id}`
      navigate(url)
    }
    const docType = AppPath.Reservations
    let accionMsg

    if (docType === AppPath.Reservations) {
      accionMsg = creandoSw ? ReservaAction.Crear : ReservaAction.Modificar
    } else {
      accionMsg = creandoSw ? NvvAction.Crear : NvvAction.Modificar
    }
    const msg = MessageCrea(docType, accionMsg)

    setInfoMessage(msg)
    setModificandoSw(false)
  }

  const reservationFill = () => {
    if (!formData) return null
    // Tenemos que transformar lo que tenemos en
    // las listas que soporta la reserva
    //    reservationEx.additionals = transformToAdditional(costs);
    formData.additionals = costs
    formData.payments = payments
    formData.tradeIns = transformToTradeIn(vehicles)
    // Los demas  campos  estan en la inicializacion o
    // se mantienen en el formulario
  }

  const onInfoTimeout = (): void => {
    setInfoMessage('')
  }
  // The callback to go back to the edit view
  const handleGoBack = () => {
    setShowPreview(false)
    setAlertMessage('')
  }
  // The callback to go back to the edit view
  const handleGoBackEdit = () => {
    setModificandoSw(true)
    setShowPreview(false)
  }

  const sumAddPagosVeh = (
    updatedCosts: Additional[] | null,
    updatedPayments: Payment[] | null,
    updatedVehicles: PayVehicle[] | null
  ) => {
    // Primero, sumemos todos los costo

    const costArray = updatedCosts ?? costs
    const suma = costArray.reduce((sum, current) => sum + Number(current?.cost ?? 0), 0)

    const payArray = updatedPayments ?? payments
    const resta = payArray.reduce((sum, current) => sum + Number(current?.value ?? 0), 0)

    const vehArray = updatedVehicles ?? vehicles
    const restaVeh = vehArray.reduce((sum, current) => sum + Number(current?.price ?? 0), 0)

    return Number(suma) - Number(resta) - Number(restaVeh)
  }

  const onCancelModify = () => {
    setModificandoSw(false)
  }
  // Si noviene la reserva, estamos creando
  const creandoSw = !reservation || nvvInd === NvvAction.CreaFromRes

  // Aca llegamos cuando hay click en Crear en la grilla de vehiculos
  // Debemos invocar a <VehicleEdit/>

  const onVehicleCreate = () => {
    setVehicleShow(true)
  }

  // Aca cuando volvemos de crear el vehiculo
  const onVehicleCreateReturn = async (id: string) => {
    setVehicleShow(false)

    // Leemos el vehiculo
    if (!id || id === '') {
      return
    }

    const veh = await fetchVehicle(id)

    if (!errorVehicle) {
      setVehVehicle(veh)
      setVehShowVehicleEdit(true)
    } else {
      setVehVehicle(null)
      // setWarningMessage("No existe vehículo con esa patente para usar como parte de pago.")
    }
  }

  //Click en botón Completar. Se va a la nota de venta
  const handleCompletar = () => {
    const id = reservation!._id
    navigate(`/${AppPath.SaleNotes}/create/from/${id}`)
  }

  const esAnulada = !!reservation?.voidedAt
  const esCompletada = !!reservation?.completedAt
  const esArchivada = !!reservation?.archivedAt

  return (
    <>
      {vehicleShow ? (
        <VehicleEdit mode={false} onReturn={onVehicleCreateReturn} />
      ) : (
        <>
          {!showPreview && (creandoSw || modificandoSw) ? (
            <div>
              {(!creandoSw || nvvInd === NvvAction.CreaFromRes) && (
                <div className="flex flex-col space-y-2  items-center mb-4">
                  <div className="flex space-x-4">
                    <div className="border flex items-center justify-center pr-4">
                      <VehicleCard vehicle={vehicle} />
                    </div>
                    <div className="border flex items-center justify-center pr-4 pl-4">
                      <ClientCard client={client} />
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {creandoSw ? (
                  <div>
                    <InputField
                      label="Número"
                      name="number"
                      value={formData?.number}
                      type="number"
                      handleInputChange={handleInputChange}
                    />
                    <div className="text-sm">Deja en cero para numeración automática</div>
                  </div>
                ) : (
                  <DisplayValueVert label="Número" name="number" value={formData?.number} />
                )}

                <InputField
                  label="Precio Venta"
                  name="price_format"
                  value={formData?.price_format}
                  type="string"
                  handleInputChange={handleInputChange}
                />
                <InputField
                  label="Fecha"
                  name="date"
                  value={formData?.date}
                  type="date"
                  handleInputChange={handleInputChange}
                />

                <div className="text-right">
                  <DisplayValueVertRes label="Saldo" value={formData?.amount_format} />
                </div>
              </div>
              <div className="border  shadow-lg rounded-md p-2 mt-2">
                <ReservationAdicional
                  title="Adicionales"
                  vehiclePrice={formData?.price ?? 0}
                  appraisal={appraisal}
                  array={costs}
                  onPaymentsChange={handleCostsChange}
                />
              </div>
              <TotalDisplay title="Total a pagar" total={totalCost} />

              <div className="border shadow-lg rounded-md p-2 mt-2">
                <ReservationPago title="Pagos" array={payments} onPaymentsChange={handlePaymentsChange} />
              </div>

              <div className="border shadow-lg rounded-md p-2 mt-2">
                <ReservationVehiculo
                  title="Vehículos"
                  array={vehicles}
                  patenteReservada={vehicle.plate}
                  onVehicleCreate={onVehicleCreate}
                  onPaymentsChange={handleVehiclesChange}
                  vehicleMakeDescription={vehicleMakeDescription}
                  vehShowVehicleEdit={vehShowVehicleEdit}
                  setVehShowVehicleEdit={setVehShowVehicleEdit}
                  vehNewPayment={vehNewPayment}
                  setVehNewPayment={setVehNewPayment}
                  vehErrors={vehErrors}
                  setVehErrors={setVehErrors}
                  vehVehicle={vehVehicle}
                  setVehVehicle={setVehVehicle}
                />
              </div>
              <TotalDisplay title="Total pagos" total={totalPayments} />

              <InputTextArea
                label="Observaciones"
                name="notes"
                value={formData?.notes}
                handleInputChange={handleInputChange}
              />
            </div>
          ) : (
            <>
              {formData && (
                <ReservationPreview
                  vehicle={vehicle}
                  client={client}
                  reservation={formData}
                  creandoSw={creandoSw}
                  modificandoSw={modificandoSw}
                  onGoBack={handleGoBack}
                  onCrearClick={handleCrearOModificar}
                  onCancelModify={onCancelModify}
                  nvvInd={nvvInd}
                />
              )}
              {!creandoSw && !modificandoSw && formData && (
                <>
                  <div className="mb-4">
                    <ButtonPrint />
                  </div>
                  {!esAnulada && !esArchivada && (
                    <div className="print:hidden">
                      <div>
                        {!esCompletada && handleAnular && handleArchivar && (
                          <SubMenu
                            handleGoBackEdit={handleGoBackEdit}
                            handleCompletar={handleCompletar}
                            handleAnular={handleAnular}
                            handleArchivar={handleArchivar}
                          />
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}
          {!showPreview && (creandoSw || modificandoSw) && (
            <div className="flex justify-center mt-4">
              <ButtonAction label="Vista Previa" handleButtonClick={vistaPreviaClick} />
            </div>
          )}

          <div className="mb-10">
            <DisplayError msg={alertMessage} />
          </div>
          <Info message={infoMessage} trigger={Date.now()} onTimeout={onInfoTimeout} />
        </>
      )}
    </>
  )
}

// Despliega el Label sobre el valor. Coloca borde rojo
// si el primer caracter es "-".
export const DisplayValueVertRes: React.FC<{ label: string; value?: string }> = ({ label, value }) => {
  const myClassName = 'flex flex-col'
  const signo = value?.startsWith('-')
  const borderColor = signo ? 'ring-4 ring-red-500' : ''

  return (
    <div className={myClassName}>
      <label className="font-semibold mb-1 text-center">{label}</label>
      <label className={`border rounded p-2 ${borderColor}`}>{value}</label>
    </div>
  )
}

//Submenu con los botones para "Modificar", "Completar", "Archivar", "Anular"
const SubMenu: React.FC<{
  handleGoBackEdit: () => void
  handleCompletar: () => void
  handleAnular: (motivo: string) => void
  handleArchivar: () => void
}> = ({ handleGoBackEdit, handleCompletar, handleAnular, handleArchivar }) => {
  const [clickedIndex, setClickedIndex] = useState<number>(-1)
  const [motivo, setMotivo] = useState<string>('')

  const handleMotivoChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { value } = e.target

    setMotivo(value)
  }
  const handleMenuClick = (estado: number) => {
    setClickedIndex(estado)
  }
  const handleAnularReserva = async () => {
    handleAnular && handleAnular(motivo)

    setClickedIndex(-1)
  }
  const handleArchivarReserva = async () => {
    handleArchivar && handleArchivar()

    setClickedIndex(-1)
  }
  useEffect(() => {
    // Scroll al final
    window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' })
  }, [clickedIndex])
  const buttonCaptions = ['Modificar', 'Completar', 'Archivar', 'Anular']

  const anulaTitle =
    'Al anular la reserva quedará inactiva, no podrá ser restaurada y el vehículo ya no estará reservado.'
  const archivaTitle = 'Al archivar la reserva quedará inactiva y el vehículo ya no estará reservado.'

  return (
    <div className="p-2 max-w-3xl mx-auto shadow-md border">
      <ButtonMenuSet captions={buttonCaptions} clickedIndex={clickedIndex} handleButtonClick={handleMenuClick} />
      <div className="mt-4">
        {clickedIndex === 0 && (
          <>
            <div>
              Podrás modificar los datos de esta reserva, excepto el cliente y el vehículo. Si deseas modificarlos,
              debes anular esta reserva y crear otra.
            </div>
            <div className="flex justify-center mt-4">
              <ButtonAction label="Ir a modificar" handleButtonClick={handleGoBackEdit} />
            </div>
          </>
        )}
        {clickedIndex === 1 && (
          <div>
            Serás llevado a crear la nota de venta para este vehículo y cliente, agregando el total de esta reserva como
            pago. Una vez creada la nota de venta, esta reserva será marcada como completa.
            <DocConfirma
              docType={AppPath.Reservations}
              handleCrearClick={handleCompletar}
              action={ReservaAction.Completar}
            />
          </div>
        )}
        {clickedIndex === 2 && (
          <ButtonCheckConfirma
            title={archivaTitle}
            subTitle=""
            checkLabel="Estoy seguro de archivar esta reserva."
            buttonLabel="Archivar"
            handleCrearClick={handleArchivarReserva}
          />
        )}
        {clickedIndex === 3 && (
          <ButtonCheckConfirma
            title={anulaTitle}
            subTitle="Motivo de anulación"
            checkLabel="Estoy seguro de anular esta reserva."
            buttonLabel="Anular"
            handleCrearClick={handleAnularReserva}
            handleTextAreaChange={handleMotivoChange}
            textAreaName="motivo"
            textAreaValue={motivo}
          />
        )}
      </div>
    </div>
  )
}

const vehicleMakeDescription = (vehicle: Vehicle | null): string =>
  vehicle && vehicle !== null
    ? `${vehicle?.year} ${vehicle?.brand} ${vehicle?.model} ${vehicle?.version} ${vehicle?.color} [${vehicle?.plate}]`
    : 'desconocido'

const reservationInitial = (
  userData: UserData | null,
  vehicle: Vehicle,
  client: Client,
  reservation?: Reservation,
  deal?: Deal
): ReservationFormat => {
  const today = new Date()
  const today_format = formatDateToYYYY_MM_DD(today)

  const price = vehicle.price
  const amount = price
  let profile_name = ''
  let user_phone = ''

  if (reservation) {
    profile_name = reservation.profile_name ?? ''
    user_phone = reservation.user_phone ?? ''
  } else {
    profile_name = deal?.profile_user ?? userData?.user?.fullname ?? ''
  }

  return {
    _id: reservation?._id ?? '',
    number: reservation?.number ?? 0,
    amount: amount,
    amount_format: formatWithThousandSep(amount),
    price: price,
    date: today_format,
    createdAt: '',
    notes: '',
    organization: userData?.organization?._id ?? '',
    profile: deal?.profile ?? userData?.profile?._id ?? '',
    client: client._id,
    vehicle: vehicle._id,
    price_format: formatWithThousandSep(price),
    footer: reservation?.footer ?? '',
    completedAt: '',
    voidedAt: '',
    voidedCause: '',
    voidedBy_user: '',
    profile_name: profile_name,
    user_phone: user_phone,
  }
}
